import { useMemo } from "react";
import { Card, Group, Stack, Text, Title } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import type { VectorMetadata, TilesetMetadata } from "@project/api";
import { useQuery } from "@tanstack/react-query";
import { tilesetDashboardLayoutRoute } from "./routes";
import { usePathAccountName } from "../../../hooks/router";

export function TilesetVectorMetadataSummary({ metadata }: { metadata: VectorMetadata }) {
  return (
    <Stack>
      <Title size="h5">
        <FormattedMessage defaultMessage="Vector layers" id="title.vectorLayers" />
      </Title>
      {metadata.vectorLayers.map((layer) => {
        const tileStats = metadata.tilestats.layers.find(l => l.layer === layer.id);
        return (
          <Card key={layer.id} withBorder>
            <Stack>
              <Title size="h5">
                Id:
                {layer.id}
              </Title>
              <Text>
                Description:
                {" "}
                {layer.description}
              </Text>
            </Stack>
            <Group grow align="flex-start">
              <Stack gap="4px" pl={32}>
                <Title size="h5" pt="12px">
                  <FormattedMessage defaultMessage="Fields" id="title.fields" />
                </Title>
                {Object.entries(layer.fields).map(([fieldName, field]) => {
                  return (
                    <Text key={fieldName}>
                      {fieldName}
                      {" "}
                      {field}
                    </Text>
                  );
                })}
              </Stack>
              <Stack gap="4px" pl={32}>
                <Title size="h5" pt="12px">
                  <FormattedMessage defaultMessage="Zoom" id="title.zoom" />
                </Title>
                <Text>
                  Min zoom:
                  {" "}
                  {layer.minZoom}
                </Text>
                <Text>
                  Max zoom:
                  {" "}
                  {layer.maxZoom}
                </Text>
              </Stack>
              <Stack gap="4px">
                <Title size="h5">
                  <FormattedMessage defaultMessage="Stats" id="title.stats" />
                </Title>
                {tileStats && (
                  <Text>
                    <FormattedMessage defaultMessage="Count" id="title.count" />
                    {" "}
                    {tileStats.count}
                  </Text>
                )}
                {tileStats && (
                  <Text>
                    <FormattedMessage defaultMessage="Geometry" id="title.geometry" />
                    {" "}
                    {tileStats.geometry}
                  </Text>
                )}
                {tileStats && (
                  <Text>
                    <FormattedMessage defaultMessage="Attribute count" id="title.attributeCount" />
                    {" "}
                    {tileStats.attributeCount}
                  </Text>
                )}
              </Stack>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}

export function TilesetMeta() {
  const intl = useIntl();
  const trpc = useTRPC();
  const account = usePathAccountName();
  const { tileset } = tilesetDashboardLayoutRoute.useParams();
  const { dataset } = tilesetDashboardLayoutRoute.useSearch();

  const { data: current } = useQuery(trpc.getTilesetAndVersion.queryOptions({
    tileset,
    dataset,
    account,
  }, {
    refetchInterval: 1000 * 60 * 5,
    refetchOnMount: false,
  }));

  const metadata = useMemo(() => {
    return current?.version?.metadata as TilesetMetadata | undefined;
  }, [current]);

  return (
    <Stack px="md" my="md" gap={16}>
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Metadata", id: "navigation.metadata" })}
        current={dataset ? `${dataset} - ${tileset}` : tileset}
        description={
          <FormattedMessage id="text.tilesetMetadataDescription" defaultMessage="View your tileset metadata." />
        }
      />

      <Stack>
        {metadata?.meta_format === "vector" && (
          <TilesetVectorMetadataSummary metadata={metadata} />
        )}
      </Stack>
    </Stack>
  );
}
